import { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2, BarChart3, Users, Calendar, TrendingUp, TrendingDown,
  Search, User, Briefcase,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function LMSCapacity() {
  const [capacity, setCapacity] = useState(null);
  const [structure, setStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchMember, setSearchMember] = useState("");
  const [memberStatus, setMemberStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    Promise.all([fetchCapacity(), fetchStructure()]).finally(() => setLoading(false));
  }, []);

  const fetchCapacity = async () => {
    try {
      const res = await axios.get(`${API}/lms/capacity`, {
        params: { months: 6 },
        headers: getHeaders(), withCredentials: true,
      });
      setCapacity(res.data);
    } catch {}
  };

  const fetchStructure = async () => {
    try {
      const res = await axios.get(`${API}/lms/structure`, {
        headers: getHeaders(), withCredentials: true,
      });
      setStructure(res.data);
    } catch {}
  };

  const handleSearchMember = async () => {
    if (!searchMember.trim()) return;
    setStatusLoading(true);
    try {
      const res = await axios.get(`${API}/lms/check/${searchMember}`, {
        params: { date: new Date().toISOString().split("T")[0] },
        headers: getHeaders(), withCredentials: true,
      });
      setMemberStatus(res.data);
    } catch {
      toast.error("Member not found");
      setMemberStatus(null);
    }
    setStatusLoading(false);
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center min-h-[400px]">
      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
    </div>;
  }

  const maxCapacity = Math.max(...(capacity?.monthly?.map((m) => m.capacity_pct) || [100]), 100);

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-lg font-semibold text-slate-800">Team Capacity Board</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Monthly Trend */}
        <div className="bg-white border border-slate-200 p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Monthly Capacity Trend
          </h3>
          <div className="space-y-2">
            {capacity?.monthly?.map((m) => (
              <div key={m.month_index} className="flex items-center gap-3">
                <span className="text-[10px] font-medium text-slate-500 w-12">{m.month}</span>
                <div className="flex-1 h-4 bg-slate-100 rounded-none overflow-hidden">
                  <div
                    className={`h-full rounded-none transition-all ${
                      m.capacity_pct >= 80 ? "bg-emerald-500"
                        : m.capacity_pct >= 60 ? "bg-amber-500"
                        : "bg-red-500"
                    }`}
                    style={{ width: `${m.capacity_pct}%` }}
                  />
                </div>
                <span className="text-[10px] font-semibold text-slate-600 w-10 text-right">{m.capacity_pct}%</span>
              </div>
            ))}
          </div>
          {capacity && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-4 text-[10px] text-slate-500">
              <span>Avg: <strong>{capacity.avg_capacity}%</strong></span>
              <span>Period: {capacity.monthly?.[0]?.month} - {capacity.monthly?.[capacity.monthly.length - 1]?.month}</span>
            </div>
          )}
        </div>

        {/* Structure Tree */}
        <div className="bg-white border border-slate-200 p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Structure Presence Check
          </h3>

          {/* Member search */}
          <div className="flex items-center gap-2 mb-4">
            <Input value={searchMember} onChange={(e) => setSearchMember(e.target.value)}
              placeholder="Enter member ID to check..."
              className="h-8 text-xs rounded-none flex-1"
              onKeyDown={(e) => e.key === "Enter" && handleSearchMember()}
            />
            <Button onClick={handleSearchMember} disabled={statusLoading}
              className="h-8 text-xs rounded-none bg-emerald-600 hover:bg-emerald-500">
              {statusLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
            </Button>
          </div>

          {memberStatus && (
            <div className={`p-3 mb-3 border text-xs ${
              memberStatus.on_leave
                ? "bg-red-50 border-red-200 text-red-700"
                : "bg-emerald-50 border-emerald-200 text-emerald-700"
            }`}>
              <p className="font-medium">
                {memberStatus.on_leave ? "On Leave" : "Present"}
              </p>
              {memberStatus.leave_detail && (
                <p className="text-[10px] mt-1">
                  {memberStatus.leave_detail.leave_type_name} &middot;
                  {memberStatus.leave_detail.start_date} to {memberStatus.leave_detail.end_date}
                </p>
              )}
            </div>
          )}

          {/* Department Structure */}
          <div className="max-h-[300px] overflow-auto">
            <p className="text-[10px] font-medium text-slate-400 mb-2">Department Members</p>
            {structure?.members?.filter((m) => m.supervisor_member_id === structure.root?.id).map((m) => (
              <div key={m.id} className="flex items-center justify-between py-1.5 px-2 hover:bg-app rounded">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
                    <User className="w-3 h-3 text-slate-500" />
                  </div>
                  <span className="text-xs text-slate-700">{m.name}</span>
                </div>
                <Badge className={`text-[8px] px-1 py-0 border-none ${
                  m.leave_status?.on_leave
                    ? "bg-red-100 text-red-600"
                    : "bg-emerald-100 text-emerald-600"
                }`}>
                  {m.leave_status?.on_leave ? m.leave_status.leave_type : "Present"}
                </Badge>
              </div>
            )) || (
              <p className="text-xs text-slate-400 py-4 text-center">No department members found</p>
            )}
          </div>
        </div>
      </div>

      {/* All Members Structure */}
      <div className="bg-white border border-slate-200 p-4">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
          All Members ({structure?.members?.length || 0})
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {structure?.members?.map((m) => (
            <div key={m.id} className="flex items-center justify-between p-2 border border-slate-100 hover:bg-app">
              <div className="flex items-center gap-2 min-w-0">
                <User className="w-3 h-3 text-slate-400 flex-shrink-0" />
                <span className="text-xs text-slate-700 truncate">{m.name}</span>
              </div>
              <Badge className={`text-[8px] px-1 py-0 border-none flex-shrink-0 ml-2 ${
                m.leave_status?.on_leave
                  ? "bg-red-100 text-red-600"
                  : "bg-emerald-100 text-emerald-600"
              }`}>
                {m.leave_status?.on_leave ? m.leave_status.leave_type : "Present"}
              </Badge>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
